/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, NgZone, OnChanges, OnDestroy} from '@angular/core';

import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {OBJECT_TYPE} from '../common/constants/object-type';
import template from './campaign-context-actions.component.html';
import {StarService} from '../../components/star/star.service';
import {ToastrService} from 'ngx-toastr';
import {LineItemModel} from '../line-item/line-item-api.service';
import {AdvertisingSharedModule} from '../advertising/advertising-shared.module';

@Component({
  standalone: true,
  imports: [AdvertisingSharedModule],
  selector: 'adn-campaign-context-actions',
  template
})
export class CampaignContextActionsComponent implements OnChanges, OnDestroy {
  @Input() state: SignalAdminResourcePageState<LineItemModel>;

  isStarred = false;
  starPending = false;
  private destroyed = false;

  constructor(
    @Inject(StarService) private readonly StarService: any,
    private readonly toastr: ToastrService,
    private readonly ngZone: NgZone
  ) {
  }

  get objectId(): string {
    return this.state?.model()?.id;
  }

  get objectType(): string {
    return OBJECT_TYPE.LineItem.type;
  }

  ngOnChanges(): void {
    this.refreshStar();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  toggleStar(): void {
    if (!this.objectId || this.starPending) {
      return;
    }
    this.starPending = true;
    const objectType = OBJECT_TYPE.LineItem;
    const newStarred = !this.isStarred;
    this.StarService.updateStar(this.objectId, objectType, newStarred).then(() => {
      if (!this.destroyed) {
        this.ngZone.run(() => {
          this.isStarred = newStarred;
        });
      }
    }).catch(() => {
      if (!this.destroyed) {
        this.ngZone.run(() => {
          this.refreshStar();
          this.toastr.error('Failed to update the campaign star', 'Star update failed');
        });
      }
    }).finally(() => {
      if (!this.destroyed) {
        this.ngZone.run(() => {
          this.starPending = false;
        });
      }
    });
  }

  private refreshStar(): void {
    if (!this.objectId) {
      this.isStarred = false;
      return;
    }
    this.isStarred = this.StarService.isStarred(this.objectId, OBJECT_TYPE.LineItem);
  }
}
